import { CheckCircle, ChevronLeft, Eye, Loader2, Send } from "lucide-react";
import { Button } from "./ui/button";

interface EditorHeaderProps {
  onPreview: () => void;
  onPublish: () => void;
  safeNavigate: (to: string) => void;
  projectTitle: string;
  saving: boolean;
  uploading: boolean;
  exporting?: boolean;
  lastSaved: Date;
}

export function EditorHeader({
  onPreview,
  onPublish,
  safeNavigate,
  projectTitle,
  saving,
  uploading,
  exporting,
  lastSaved,
}: EditorHeaderProps) {
  return (
    <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
      <div className="px-6 py-3 grid grid-cols-3 items-center max-w-[1600px] mx-auto">
        {/* LEFT — Back + Project Info */}
        <div className="flex items-center space-x-6">
          <button
            onClick={() => safeNavigate("/dashboard")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all font-bold text-sm"
          >
            <ChevronLeft size={16} />
            <span>Dashboard</span>
          </button>

          <div className="flex items-center gap-4 border-l border-slate-100 pl-6">
            <h1 className="text-base font-bold text-slate-900 truncate max-w-[200px]" title={projectTitle}>
              {projectTitle}
            </h1>

            {saving && (
              <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                <Loader2 size={12} className="animate-spin text-indigo-600" />
                <span>Saving changes...</span>
              </div>
            )}
          </div>
        </div>

        {/* CENTER — Brand */}
        <div
          className="flex justify-center cursor-pointer"
          onClick={() => (window.location.href = "/dashboard")}
        >
          <div className="flex items-center gap-2">
            <img src="/vidlaya-logo.png" className="w-8 h-8 object-contain" alt="VidLaya" />
            <span className="text-xl font-bold text-slate-900 tracking-tight">VidLaya</span>
          </div>
        </div>

        {/* RIGHT — Actions */}
        <div className="flex justify-end items-center gap-3">
          {!saving && lastSaved && (
            <div className="flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
              <CheckCircle size={12} />
              <span>Auto-saved at {new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={onPreview}
            className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center gap-2"
          >
            <Eye size={16} />
            Preview
          </Button>

          <Button
            onClick={onPublish}
            disabled={uploading || exporting}
            className="h-10 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-all shadow-md shadow-indigo-100 flex items-center gap-2 disabled:opacity-50"
          >
            {(uploading || exporting) ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {exporting ? "Exporting..." : "Export Video"}
          </Button>
        </div>
      </div>
    </header>
  );
}
